import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Send, EyeOff } from "lucide-react";
import { http, formatDetail, auDateTime } from "../../../portal/api";

export default function MessagesAdminTab({ engagementId }) {
  const [msgs, setMsgs] = useState(null);
  const [body, setBody] = useState("");
  const [internal, setInternal] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    http.get(`/portal/admin/engagements/${engagementId}/messages`)
      .then(({ data }) => setMsgs(data))
      .catch(() => toast.error("Could not load messages."));
  }, [engagementId]);
  useEffect(() => { load(); }, [load]);

  const send = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setBusy(true);
    try {
      await http.post(`/portal/admin/engagements/${engagementId}/messages`, { body, internal });
      setBody(""); setInternal(false);
      load();
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-messages-tab">
      {msgs === null ? (
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-stone2">Loading…</p>
      ) : (
        <div className="space-y-3 mb-6">
          {msgs.length === 0 && <p className="text-sm text-stone2 border border-line bg-surface1 p-6">No messages yet.</p>}
          {msgs.map((m, i) => (
            <div key={m.id} data-testid={`admin-message-${i}`} className={`max-w-2xl p-4 border ${m.internal ? "border-ochre/60 bg-surface1" : m.sender_role === "admin" ? "border-ember/40 bg-surface1 ml-auto" : "border-line bg-ink"}`}>
              <div className="flex items-baseline justify-between gap-4 mb-1.5">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-ochre flex items-center gap-1.5">
                  {m.internal && <EyeOff size={11} />}{m.internal ? "Internal note" : m.sender_role === "admin" ? "Bigibila" : m.sender_name}
                </span>
                <time className="font-mono text-[10px] text-stone2">{auDateTime(m.created_at)}</time>
              </div>
              <p className="text-sm text-stone2 whitespace-pre-wrap">{m.body}</p>
            </div>
          ))}
        </div>
      )}
      <form onSubmit={send} className="space-y-3">
        <textarea data-testid="admin-message-input" rows={3} className="field-dark w-full px-4 py-3 text-sm" placeholder={internal ? "Internal note — never visible in client APIs…" : "Message the client…"} value={body} onChange={(e) => setBody(e.target.value)} maxLength={5000} />
        <div className="flex items-center gap-5">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" data-testid="internal-note-checkbox" checked={internal} onChange={(e) => setInternal(e.target.checked)} className="accent-[#B44A2C]" />
            Internal Bigibila note (hidden from client)
          </label>
          <button type="submit" disabled={busy || !body.trim()} data-testid="admin-send-message-btn" className="font-mono text-xs uppercase tracking-[0.15em] px-5 py-2.5 bg-ember text-ink hover:bg-emberDeep transition-colors duration-300 disabled:opacity-50 flex items-center gap-2">
            <Send size={14} /> {internal ? "Save note" : "Send"}
          </button>
        </div>
      </form>
    </div>
  );
}
